import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Plus, Trash, ArrowLeft, FloppyDisk, PaperPlaneTilt, CalendarBlank } from '@phosphor-icons/react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import useThemeStyles from '../../../theme/useThemeStyles';
import LeadCoupleAutocomplete from '../../Common/LeadCoupleAutocomplete';
import PriceTaxToggle from '../../Common/PriceTaxToggle';
import { calculateLineItemTotals as calcLineItemTotals } from '../../../utils/taxCalculations';

function sourceClientLabel(invoice) {
  if (invoice?.manualRecipient?.name) {
    const email = invoice.manualRecipient.email ? ` (${invoice.manualRecipient.email})` : '';
    return `${invoice.manualRecipient.name}${email}`;
  }
  return invoice?.wedding?.name || 'this couple';
}

function InvoiceForm() {
  const styles = useThemeStyles();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const lineItemLabelProps = { sx: { display: { md: 'none' } } };
  const lineItemInputProps = { notched: !isMdUp };
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedBusiness } = useBusiness();
  const isEdit = Boolean(id);
  const addToId = !isEdit ? (searchParams.get('addTo') || '') : '';
  const isAddTo = Boolean(addToId);

  const [loading, setLoading] = useState(isEdit || isAddTo);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [_stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [editInvoice, setEditInvoice] = useState(null); // For display when editing
  const [sourceInvoice, setSourceInvoice] = useState(null);
  const [products, setProducts] = useState([]);
  const [productPicker, setProductPicker] = useState({
    open: false,
    productId: '',
    quantity: 1
  });
  const [formData, setFormData] = useState({
    sourceType: 'lead', // 'lead' | 'manual'
    leadId: '',
    manualRecipient: { name: '', email: '' },
    description: '',
    currency: 'GBP',
    dueDate: null,
    scheduledSendAt: null,
    lineItems: [{ productId: '', description: '', quantity: '', unitPrice: '', taxPercentage: '', subtotal: 0, taxAmount: 0, total: 0 }],
    paymentSchedule: []
  });

  const businessId = selectedBusiness?.business?._id;
  const fixedAlertSx = {
    position: 'fixed',
    top: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 32px)',
    maxWidth: 720,
    zIndex: (theme) => theme.zIndex.snackbar
  };

  const createEmptyLineItem = () => ({
    productId: '',
    description: '',
    quantity: '',
    unitPrice: '',
    taxPercentage: '',
    priceIncludesTax: false,
    subtotal: 0,
    taxAmount: 0,
    total: 0
  });

  const calculateLineItemTotals = (lineItem) => {
    const quantity = lineItem.quantity === '' || lineItem.quantity === null || lineItem.quantity === undefined ? 0 : (lineItem.quantity || 0);
    const unitPrice = lineItem.unitPrice === '' || lineItem.unitPrice === null || lineItem.unitPrice === undefined ? 0 : (lineItem.unitPrice || 0);
    const taxPercentage = lineItem.taxPercentage === '' || lineItem.taxPercentage === null || lineItem.taxPercentage === undefined ? 0 : (lineItem.taxPercentage || 0);
    const totals = calcLineItemTotals({
      quantity,
      unitPrice,
      taxPercentage,
      priceIncludesTax: lineItem.priceIncludesTax
    });

    return {
      ...lineItem,
      ...totals
    };
  };

  const fetchProducts = useCallback(async () => {
    if (!businessId) return;
    try {
      const response = await api.get(`/api/products/business/${businessId}`);
      setProducts(response.data || []);
    } catch {
      setProducts([]);
    }
  }, [businessId]);

  const checkStripeConnectStatus = useCallback(async () => {
    try {
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) return;
      
      const response = await api.get(`/api/stripe-connect/status/${businessId}`);
      setStripeConnectStatus(response.data);
    } catch (error) {
      // Ignore errors - just means status check failed
      setStripeConnectStatus(null);
    }
  }, [selectedBusiness?.business?._id]);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/invoices/${id}`);
      const invoice = response.data;
      setEditInvoice(invoice);
      const _hasWedding = invoice.wedding && (invoice.wedding._id || invoice.wedding);
      const hasManual = invoice.manualRecipient?.name && invoice.manualRecipient?.email;
      setFormData(prev => ({
        ...prev,
        sourceType: hasManual ? 'manual' : 'lead',
        leadId: '',
        manualRecipient: invoice.manualRecipient || { name: '', email: '' },
        description: invoice.description || '',
        currency: invoice.currency || 'GBP',
        dueDate: invoice.dueDate ? new Date(invoice.dueDate) : null,
        scheduledSendAt: invoice.scheduledSendAt ? new Date(invoice.scheduledSendAt) : null,
        paymentSchedule: (invoice.paymentSchedule || []).map(item => ({
          label: item.label || '',
          amount: item.amount ?? '',
          dueDate: item.dueDate ? new Date(item.dueDate) : null
        })),
        lineItems: invoice.lineItems.length > 0 
          ? invoice.lineItems.map(item => {
              const quantity = item.quantity ?? '';
              const unitPrice = item.unitPrice ?? '';
              const taxPercentage = item.taxPercentage ?? '';
              const calculatedSubtotal = (quantity || 0) * (unitPrice || 0);
              const calculatedTax = calculatedSubtotal * (taxPercentage || 0) / 100;
              const calculatedTotal = calculatedSubtotal + calculatedTax;
              
              return {
                productId: item.productId || '',
                description: item.description || '',
                quantity: quantity,
                unitPrice: unitPrice,
                taxPercentage: taxPercentage,
                priceIncludesTax: Boolean(item.priceIncludesTax),
                subtotal: item.subtotal ?? calculatedSubtotal,
                taxAmount: item.taxAmount ?? calculatedTax,
                total: item.total ?? calculatedTotal
              };
            })
          : [{ productId: '', description: '', quantity: '', unitPrice: invoice.amount || '', taxPercentage: '', subtotal: invoice.amount || 0, taxAmount: 0, total: invoice.amount || 0 }]
      }));
    } catch (err) {
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSourceInvoice = useCallback(async () => {
    if (!addToId) return;
    try {
      setLoading(true);
      const response = await api.get(`/api/invoices/${addToId}`);
      const invoice = response.data;
      setSourceInvoice(invoice);
      setFormData((prev) => ({
        ...prev,
        currency: invoice.currency || 'GBP',
        description: prev.description || `Extras for ${invoice.invoiceNumber}`
      }));
    } catch (err) {
      setError('Could not load the invoice to add to');
    } finally {
      setLoading(false);
    }
  }, [addToId]);

  useEffect(() => {
    if (businessId) {
      fetchProducts();
      checkStripeConnectStatus();
      if (isEdit) {
        fetchInvoice();
      } else if (isAddTo) {
        fetchSourceInvoice();
      }
    }
  }, [businessId, id, isEdit, isAddTo, fetchProducts, checkStripeConnectStatus, fetchInvoice, fetchSourceInvoice]);

  const handleLineItemChange = (index, field, value) => {
    const updated = [...formData.lineItems];
    
    // For number fields, allow empty string or valid number
    if (field === 'quantity' || field === 'unitPrice' || field === 'taxPercentage') {
      // Allow empty string for user to clear the field
      if (value === '' || value === null || value === undefined) {
        updated[index] = {
          ...updated[index],
          [field]: ''
        };
      } else {
        // Parse the value, but keep it as a number (not defaulting)
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          updated[index] = {
            ...updated[index],
            [field]: numValue
          };
        } else {
          // Invalid number, keep current value
          return;
        }
      }
    } else {
      updated[index] = {
        ...updated[index],
        [field]: value
      };
    }
    
    // Recalculate subtotal, tax, and total when quantity, unitPrice, or taxPercentage changes
    if (field === 'quantity' || field === 'unitPrice' || field === 'taxPercentage' || field === 'priceIncludesTax') {
      updated[index] = calculateLineItemTotals(updated[index]);
    }
    
    setFormData({ ...formData, lineItems: updated });
  };

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [...formData.lineItems, createEmptyLineItem()]
    });
  };

  const handleOpenProductPicker = () => {
    setProductPicker({
      open: true,
      productId: products[0]?._id || '',
      quantity: 1
    });
  };

  const handleCloseProductPicker = () => {
    setProductPicker((prev) => ({
      ...prev,
      open: false
    }));
  };

  const handleAddFromProducts = () => {
    if (!productPicker.productId) return;
    const product = products.find((item) => item._id === productPicker.productId);
    if (!product) return;

    const quantity = Number(productPicker.quantity) > 0 ? Number(productPicker.quantity) : 1;
    const lineItem = calculateLineItemTotals({
      ...createEmptyLineItem(),
      productId: product._id,
      description: product.name || '',
      quantity,
      unitPrice: product.price ?? '',
      taxPercentage: product.taxPercentage ?? 0,
      priceIncludesTax: Boolean(product.priceIncludesTax)
    });

    setFormData((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, lineItem]
    }));
    handleCloseProductPicker();
  };

  const handleRemoveLineItem = (index) => {
    if (formData.lineItems.length > 1) {
      const updated = formData.lineItems.filter((_, i) => i !== index);
      setFormData({ ...formData, lineItems: updated });
    }
  };

  const handleAddScheduleItem = () => {
    setFormData((prev) => ({
      ...prev,
      paymentSchedule: [...prev.paymentSchedule, { label: '', amount: '', dueDate: null }]
    }));
  };

  const handleScheduleItemChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.paymentSchedule];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, paymentSchedule: updated };
    });
  };

  const handleRemoveScheduleItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      paymentSchedule: prev.paymentSchedule.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (sendInvoice = false) => {
    try {
      setSaving(true);
      setError('');

      const hasLead = formData.sourceType === 'lead' && formData.leadId;
      const hasManual = formData.sourceType === 'manual' &&
        formData.manualRecipient?.name?.trim() &&
        formData.manualRecipient?.email?.trim();
      const hasExistingRecipient = isEdit && (
        Boolean(editInvoice?.wedding && (editInvoice.wedding._id || editInvoice.wedding)) ||
        Boolean(editInvoice?.manualRecipient?.name && editInvoice?.manualRecipient?.email)
      );
      if (!isAddTo && !hasLead && !hasManual && !hasExistingRecipient) {
        setError(formData.sourceType === 'manual'
          ? 'Please enter client name and email'
          : 'Please select a couple or choose manual invoice');
        return;
      }

      if (formData.lineItems.some(item => {
        const unitPrice = item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? 0 : (item.unitPrice || 0);
        return !item.description || unitPrice <= 0;
      })) {
        setError('Please complete all line items with description and valid price');
        return;
      }

      // Calculate grand total (subtotal + tax)
      const subtotal = formData.lineItems.reduce((sum, item) => {
        const quantity = item.quantity === '' || item.quantity === null || item.quantity === undefined ? 0 : (item.quantity || 0);
        const unitPrice = item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? 0 : (item.unitPrice || 0);
        return sum + (item.subtotal || (quantity * unitPrice));
      }, 0);
      const totalTax = formData.lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
      const totalAmount = subtotal + totalTax;
      
      if (totalAmount <= 0) {
        setError('Invoice amount must be greater than 0');
        return;
      }

      const scheduleItems = formData.paymentSchedule.filter(
        (item) => item.label?.trim() || item.amount !== '' || item.dueDate
      );
      if (scheduleItems.some((item) => !item.label?.trim() || !(parseFloat(item.amount) > 0))) {
        setError('Each instalment needs a label and an amount greater than 0');
        return;
      }
      const scheduleTotal = scheduleItems.reduce((sum, item) => sum + parseFloat(item.amount), 0);
      if (scheduleTotal > totalAmount + 0.005) {
        setError('The instalments add up to more than the invoice total');
        return;
      }

      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setError('Business not found');
        return;
      }
      
      const payload = {
        businessId,
        ...(isAddTo && { sourceInvoiceId: addToId }),
        ...(!isAddTo && formData.sourceType === 'lead' && formData.leadId && { leadId: formData.leadId }),
        ...(formData.sourceType === 'manual' && hasManual && {
          manualRecipient: {
            name: formData.manualRecipient.name.trim(),
            email: formData.manualRecipient.email.trim()
          }
        }),
        description: formData.description,
        currency: formData.currency,
        dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
        scheduledSendAt: formData.scheduledSendAt ? formData.scheduledSendAt.toISOString() : null,
        lineItems: formData.lineItems.map(item => ({
          productId: item.productId || undefined,
          description: item.description || '',
          quantity: item.quantity === '' || item.quantity === null || item.quantity === undefined ? 1 : (item.quantity || 1),
          unitPrice: item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? 0 : (item.unitPrice || 0),
          taxPercentage: item.taxPercentage === '' || item.taxPercentage === null || item.taxPercentage === undefined ? 0 : (item.taxPercentage || 0),
          priceIncludesTax: Boolean(item.priceIncludesTax),
          subtotal: item.subtotal || 0,
          taxAmount: item.taxAmount || 0,
          total: item.total || 0
        })),
        paymentSchedule: scheduleItems.map((item) => ({
          label: item.label.trim(),
          amount: parseFloat(item.amount),
          dueDate: item.dueDate ? item.dueDate.toISOString() : undefined
        }))
      };

      let response;
      if (isEdit) {
        const editPayload = {
          description: payload.description,
          currency: payload.currency,
          dueDate: payload.dueDate,
          scheduledSendAt: formData.scheduledSendAt ? formData.scheduledSendAt.toISOString() : null,
          lineItems: payload.lineItems,
          paymentSchedule: payload.paymentSchedule
        };
        if (formData.sourceType === 'manual' && hasManual) {
          editPayload.manualRecipient = payload.manualRecipient;
        }
        response = await api.put(`/api/invoices/${id}`, editPayload);
      } else {
        response = await api.post('/api/invoices', payload);
      }

      const shouldSendNow = sendInvoice ||
        (formData.scheduledSendAt && new Date(formData.scheduledSendAt).getTime() <= Date.now());

      if (shouldSendNow && response.data.status === 'draft') {
        await api.post(`/api/invoices/${response.data._id}/send`);
        navigate('/supplier/invoices');
      } else {
        navigate(`/supplier/invoices/${response.data._id}`);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container.page}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/supplier/invoices')} sx={{ mr: 1 }}>
          <ArrowLeft size={24} />
        </IconButton>
        <Typography component="h2" className="pagename" sx={styles.text.pagename}>
          {isEdit ? 'Edit Invoice' : isAddTo ? `Add to ${sourceInvoice?.invoiceNumber || 'invoice'}` : 'Create Invoice'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={fixedAlertSx} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {isAddTo ? (
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Adding extras for
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {sourceClientLabel(sourceInvoice)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {sourceInvoice?.invoiceNumber} stays as it was sent. This creates a new invoice for the extras.
                </Typography>
              </Box>
            ) : isEdit ? (
              formData.sourceType === 'manual' ? (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Client details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={formData.manualRecipient?.name || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          manualRecipient: { ...formData.manualRecipient, name: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={formData.manualRecipient?.email || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          manualRecipient: { ...formData.manualRecipient, email: e.target.value }
                        })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Client</Typography>
                  <Typography variant="body1">
                    {editInvoice?.wedding?.name ||
                      (editInvoice?.wedding?.partnerDetails?.partnerOne?.name && editInvoice?.wedding?.partnerDetails?.partnerTwo?.name
                        ? `${editInvoice.wedding.partnerDetails.partnerOne.name} & ${editInvoice.wedding.partnerDetails.partnerTwo.name}`
                        : editInvoice?.wedding?.partnerDetails?.partnerOne?.name || '—')}
                  </Typography>
                </Box>
              )
            ) : (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Invoice for</InputLabel>
                  <Select
                    value={formData.sourceType}
                    label="Invoice for"
                    onChange={(e) => setFormData({ ...formData, sourceType: e.target.value, leadId: '', manualRecipient: { name: '', email: '' } })}
                  >
                    <MenuItem value="lead">From My Couples</MenuItem>
                    <MenuItem value="manual">Manual invoice</MenuItem>
                  </Select>
                </FormControl>
                {formData.sourceType === 'lead' ? (
                  <LeadCoupleAutocomplete
                    businessId={businessId}
                    value={formData.leadId}
                    onChange={(leadId) => setFormData({ ...formData, leadId: leadId || '' })}
                    label="Couple"
                    required
                  />
                ) : (
                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Client details</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          value={formData.manualRecipient?.name || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            manualRecipient: { ...formData.manualRecipient, name: e.target.value }
                          })}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={formData.manualRecipient?.email || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            manualRecipient: { ...formData.manualRecipient, email: e.target.value }
                          })}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                disabled={isAddTo}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="EUR">EUR (€)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Due Date"
                value={formData.dueDate}
                onChange={(newValue) => setFormData({ ...formData, dueDate: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
                format="dd/MM/yyyy"
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
              <DatePicker
                label="Schedule Send (optional)"
                value={formData.scheduledSendAt}
                onChange={(newValue) => setFormData({ ...formData, scheduledSendAt: newValue })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: formData.scheduledSendAt ? 'Invoice will be sent automatically on this date' : 'Leave empty to save as draft'
                  }
                }}
                format="dd/MM/yyyy"
                minDate={new Date()}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>

            <Grid
              container
              spacing={2}
              sx={{
                display: { xs: 'none', md: 'flex' },
                mb: 1,
                px: 0.5
              }}
            >
              <Grid item md={4}>
                <Typography variant="caption" color="text.secondary">Description</Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="caption" color="text.secondary">Qty</Typography>
              </Grid>
              <Grid item md={2}>
                <Typography variant="caption" color="text.secondary">Unit price</Typography>
              </Grid>
              <Grid item md={1.5}>
                <Typography variant="caption" color="text.secondary">Tax %</Typography>
              </Grid>
              <Grid item md={1.5}>
                <Typography variant="caption" color="text.secondary">VAT</Typography>
              </Grid>
              <Grid item md={1}>
                <Typography variant="caption" color="text.secondary">Total</Typography>
              </Grid>
              <Grid item md={1} />
            </Grid>

            {formData.lineItems.map((item, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'flex-start' }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={item.description}
                    onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                    required
                    InputLabelProps={lineItemLabelProps}
                    InputProps={lineItemInputProps}
                  />
                </Grid>
                <Grid item xs={4} md={1}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qty"
                    value={item.quantity === '' || item.quantity === null || item.quantity === undefined ? '' : item.quantity}
                    onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    InputLabelProps={lineItemLabelProps}
                    InputProps={lineItemInputProps}
                  />
                </Grid>
                <Grid item xs={8} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Unit price"
                    value={item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? '' : item.unitPrice}
                    onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                    InputLabelProps={lineItemLabelProps}
                    InputProps={lineItemInputProps}
                  />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Tax %"
                    value={item.taxPercentage === '' || item.taxPercentage === null || item.taxPercentage === undefined ? '' : (item.taxPercentage || 0)}
                    onChange={(e) => handleLineItemChange(index, 'taxPercentage', e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    helperText={item.taxPercentage > 0 ? `${item.taxAmount?.toFixed(2) || '0.00'} VAT` : ' '}
                    FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                    InputLabelProps={lineItemLabelProps}
                    InputProps={lineItemInputProps}
                  />
                </Grid>
                <Grid item xs={6} md={1.5}>
                  <PriceTaxToggle
                    value={Boolean(item.priceIncludesTax)}
                    onChange={(priceIncludesTax) => handleLineItemChange(index, 'priceIncludesTax', priceIncludesTax)}
                  />
                </Grid>
                <Grid item xs={10} md={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', md: 'none' }, mb: 0.75 }}
                  >
                    Total
                  </Typography>
                  <Box
                    sx={{
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 0.5
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}
                    >
                      {(item.total || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ minHeight: 20, mt: 0.5 }} aria-hidden />
                </Grid>
                <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: { xs: 0, md: 0.5 } }}>
                  <IconButton
                    onClick={() => handleRemoveLineItem(index)}
                    disabled={formData.lineItems.length === 1}
                    color="error"
                    size="small"
                  >
                    <Trash size={18} />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Box sx={{ mt: 2, mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                startIcon={<Plus size={18} />}
                onClick={handleAddLineItem}
                size="small"
                variant="outlined"
              >
                Add manual item
              </Button>
              <Button
                startIcon={<Plus size={18} />}
                onClick={handleOpenProductPicker}
                size="small"
                variant="outlined"
                disabled={products.length === 0}
              >
                Add from products
              </Button>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'right' }}>
              {(() => {
                const subtotal = formData.lineItems.reduce((sum, item) => {
                  const quantity = item.quantity === '' || item.quantity === null || item.quantity === undefined ? 0 : (item.quantity || 0);
                  const unitPrice = item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? 0 : (item.unitPrice || 0);
                  return sum + (item.subtotal || (quantity * unitPrice));
                }, 0);
                const totalTax = formData.lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
                const grandTotal = subtotal + totalTax;
                
                return (
                  <>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Subtotal: {new Intl.NumberFormat('en-GB', {
                          style: 'currency',
                          currency: formData.currency
                        }).format(subtotal)}
                      </Typography>
                    </Box>
                    {totalTax > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Tax: {new Intl.NumberFormat('en-GB', {
                            style: 'currency',
                            currency: formData.currency
                          }).format(totalTax)}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="h6">
                      Total: {new Intl.NumberFormat('en-GB', {
                        style: 'currency',
                        currency: formData.currency
                      }).format(grandTotal)}
                    </Typography>
                  </>
                );
              })()}
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 0.5 }}>Payment Schedule (optional)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Break the invoice into expected instalments, for example a deposit and a final balance. Payments are recorded against the invoice as they come in.
            </Typography>

            {formData.paymentSchedule.map((item, index) => (
              <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="E.g Deposit or Final Payment"
                    value={item.label}
                    onChange={(e) => handleScheduleItemChange(index, 'label', e.target.value)}
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Amount"
                    value={item.amount}
                    onChange={(e) => handleScheduleItemChange(index, 'amount', e.target.value)}
                    inputProps={{ min: 0.01, step: 0.01 }}
                  />
                </Grid>
                <Grid item xs={5} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                    <DatePicker
                      label="Due date"
                      value={item.dueDate}
                      onChange={(newValue) => handleScheduleItemChange(index, 'dueDate', newValue)}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      format="dd/MM/yyyy"
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={2} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <IconButton
                    onClick={() => handleRemoveScheduleItem(index)}
                    color="error"
                    size="small"
                  >
                    <Trash size={18} />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Button
              startIcon={<Plus size={18} />}
              onClick={handleAddScheduleItem}
              size="small"
              variant="outlined"
            >
              Add instalment
            </Button>

            {/* Fee breakdown temporarily hidden - payments feature coming soon */}
            {/* {stripeConnectStatus?.connected && stripeConnectStatus?.chargesEnabled && (() => {
              const subtotal = formData.lineItems.reduce((sum, item) => {
                const quantity = item.quantity === '' || item.quantity === null || item.quantity === undefined ? 0 : (item.quantity || 0);
                const unitPrice = item.unitPrice === '' || item.unitPrice === null || item.unitPrice === undefined ? 0 : (item.unitPrice || 0);
                return sum + (item.subtotal || (quantity * unitPrice));
              }, 0);
              const totalTax = formData.lineItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0);
              const grandTotal = subtotal + totalTax;
              
              return (
                <FeeBreakdown
                  fees={{
                    stripeFee: (grandTotal * 0.029) + 0.30,
                    platformFee: 3.00,
                    totalFees: (grandTotal * 0.029) + 0.30 + 3.00,
                    netAmount: grandTotal - ((grandTotal * 0.029) + 0.30 + 3.00)
                  }}
                  amount={grandTotal}
                  currency={formData.currency}
                />
              );
            })()} */}
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button onClick={() => navigate('/supplier/invoices')}>
            Cancel
          </Button>
          {formData.scheduledSendAt ? (
            <Button
              variant="contained"
              startIcon={<CalendarBlank size={18} />}
              onClick={() => handleSubmit(false)}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : (isEdit ? 'Update Schedule' : 'Schedule')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<FloppyDisk size={18} />}
              onClick={() => handleSubmit(false)}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Draft'}
            </Button>
          )}
          {isEdit && (
            <Button
              variant="contained"
              startIcon={<PaperPlaneTilt size={18} />}
              onClick={() => handleSubmit(true)}
              disabled={saving}
            >
              Send Invoice
            </Button>
          )}
          {!isEdit && (
            <Button
              variant="contained"
              startIcon={<PaperPlaneTilt size={18} />}
              onClick={() => handleSubmit(true)}
              disabled={saving}
            >
              Create & Send
            </Button>
          )}
        </Box>

        <Dialog open={productPicker.open} onClose={handleCloseProductPicker} maxWidth="xs" fullWidth>
          <DialogTitle>Add from products</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1, display: 'grid', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Product or service</InputLabel>
                <Select
                  value={productPicker.productId}
                  label="Product or service"
                  onChange={(e) => setProductPicker((prev) => ({ ...prev, productId: e.target.value }))}
                >
                  {products.map((product) => (
                    <MenuItem key={product._id} value={product._id}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantity"
                value={productPicker.quantity}
                onChange={(e) => setProductPicker((prev) => ({ ...prev, quantity: e.target.value }))}
                inputProps={{ min: 1, step: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseProductPicker}>Cancel</Button>
            <Button onClick={handleAddFromProducts} variant="contained" disabled={!productPicker.productId}>
              Add item
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
}

export default InvoiceForm;
